import React, {useMemo, useState} from 'react';
import {List, Map, Set} from 'immutable';
import {Moment} from 'moment';
import Grid from '@material-ui/core/Grid';
import {makeStyles} from '@material-ui/core/styles';
import {AxisState, MetricData, MetricInfo, MetricParams, RepChartPoint} from '@store/@types/metric';
import MetricsActions from '@store/metrics-trends/NineBoxMetricActions';
import User, {UserId} from 'records/User';
import {NineBoxGridWrapper} from 'components/dashboard/manager/nine-box/NineBoxGridWrapper';
import {getIsCoachingZone} from '@store/metrics-trends/metrics-trends.consts';
import {MultiRepChart} from 'components/dashboard/manager/nine-box/MultiRepChart';
import {KpiTarget} from '@store/@types/kpi';
import {IdMap} from '@store/@types/util';
import CoachingPlan, {PlanId} from 'records/CoachingPlan';
import {OneRepChart} from 'components/dashboard/manager/nine-box/one-rep-chart/OneRepChart';
import {
  coachingLineData,
  repChartById,
  repSkillChanges,
} from 'components/dashboard/manager/nine-box/one-rep-chart/OneRepData.utils';
import {NineBoxSidebar} from 'components/dashboard/manager/nine-box/NineBoxSidebar';
import {NineBoxHeader} from 'components/dashboard/manager/nine-box/NineBoxHeader';
import isEmpty from 'lodash/isEmpty';
import {CoachingPlanVelocity} from 'records/CoachingPlanVelocity';
import {GridBordered} from '@components/grid/GridBordered';
import {getGraphRangeLabels} from 'components/dashboard/manager/nine-box/GraphRange.utils';
import {getGoals} from 'components/dashboard/manager/nine-box/NineBox.utils';
import {associateBy} from 'utils/associateBy';
import {TeamId} from 'records/TeamId';
import {CHART_DATA_FILTER} from 'components/dashboard/manager/nine-box/NineBox.const';

interface NineBoxProps {
  chartData: RepChartPoint[];
  xAxisOptions: MetricInfo[];
  yAxisOptions: MetricInfo[];
  titleMap: Map<MetricParams, string>;
  kpisById: IdMap<KpiTarget>;
  metricsIsLoading: boolean;
  setAxisMetric: typeof MetricsActions.setAxisMetric;
  onCreatePlan: (rep: User) => void;
  managerToDisplay: User;
  ownSummary: boolean;
  startDate: Moment;
  endDate: Moment;
  teams: Set<TeamId>;
  users: Map<UserId, User>;
  coachingRepIds: Set<UserId>;
  horizontal: AxisState;
  vertical: AxisState;
  selectedDataPoint: RepChartPoint | undefined;
  onSelectRep: (rep: UserId | null) => void;
  repPlans: List<CoachingPlan>;
  timeSpentByPlan: Map<PlanId, number>;
  meetingTime: MetricData;
  reviewTime: MetricData;
  velocityMap: Map<PlanId, CoachingPlanVelocity>;
  repDataLoading: boolean;
  adminId?: string;
}

export const NineBox: React.FC<NineBoxProps> = ({
  startDate,
  endDate,
  teams,
  users,
  coachingRepIds,
  horizontal,
  vertical,
  setAxisMetric,
  chartData,
  metricsIsLoading,
  xAxisOptions,
  yAxisOptions,
  onCreatePlan,
  managerToDisplay,
  ownSummary,
  kpisById,
  titleMap,
  selectedDataPoint,
  onSelectRep,
  repPlans,
  timeSpentByPlan,
  meetingTime,
  reviewTime,
  velocityMap,
  repDataLoading,
  adminId,
}) => {
  const [planHighlight, setPlanHighlight] = useState<PlanId | undefined>();
  const [repFilter, setRepFilter] = useState(CHART_DATA_FILTER.ALL);
  const sidebar = document.getElementById('right-sidebar');
  const repId = selectedDataPoint?.rep.id;
  const graphLabels = getGraphRangeLabels(repId, horizontal, vertical, kpisById);

  const multiRepData = useMemo(() => {
    const filteredByTeam = !teams.isEmpty()
      ? chartData.filter(el => teams.has(el.rep.teamId))
      : chartData;
    switch (repFilter) {
      case CHART_DATA_FILTER.ALL:
        return filteredByTeam;
      case CHART_DATA_FILTER.MANAGER_COACHING:
        return filteredByTeam.filter(data => coachingRepIds.has(data.rep.id));
      case CHART_DATA_FILTER.MANAGER_NOT_COACHING:
        return filteredByTeam.filter(data => !coachingRepIds.has(data.rep.id));
    }
  }, [chartData, repFilter, teams, coachingRepIds]);

  const {xGoal, yGoal} = useMemo(
    () => getGoals(horizontal, vertical, kpisById),
    [horizontal, vertical, kpisById]
  );

  const oneRepData = repId
    ? repChartById(repId, horizontal, vertical, kpisById, titleMap)
    : undefined;
  const noData = selectedDataPoint ? !oneRepData : isEmpty(multiRepData);

  const {nineBoxWrap, root} = useStyles();
  return (
    <div className={root}>
      <Grid container spacing={3}>
        <GridBordered item xs={sidebar ? 12 : 8} md={sidebar ? 12 : 9}>
          <NineBoxHeader
            selectedDataPoint={selectedDataPoint}
            onSelectRep={onSelectRep}
            repFilter={repFilter}
            setRepFilter={setRepFilter}
            ownSummary={ownSummary}
            managerToDisplay={managerToDisplay}
            users={users}
            teams={teams}
            startDate={startDate}
            endDate={endDate}
            exportOptions={associateBy([...xAxisOptions, ...yAxisOptions], x => x.value).toList()}
          />
          <div className={nineBoxWrap}>
            <NineBoxGridWrapper
              isEmpty={noData}
              isLoading={metricsIsLoading}
              xAxisOptions={xAxisOptions}
              yAxisOptions={yAxisOptions}
              horizontalParams={horizontal.metric}
              verticalParams={vertical.metric}
              setAxisMetric={setAxisMetric}
              xRange={graphLabels?.xRange}
              yRange={graphLabels?.yRange}>
              {selectedDataPoint && repId ? (
                <OneRepChart
                  lineData={coachingLineData(startDate, endDate, repPlans, timeSpentByPlan)}
                  chartData={oneRepData}
                  xGoal={xGoal}
                  yGoal={yGoal}
                  data={selectedDataPoint}
                  onCreatePlan={() => onCreatePlan(selectedDataPoint.rep)}
                  startDate={startDate}
                  endDate={endDate}
                  setPlanHighlight={setPlanHighlight}
                />
              ) : (
                <MultiRepChart
                  xGoal={xGoal}
                  yGoal={yGoal}
                  isCoachingZone={getIsCoachingZone({
                    horizontal: horizontal.metric,
                    vertical: vertical.metric,
                  })}
                  chartData={multiRepData}
                  onSelectRep={onSelectRep}
                  onCreatePlan={onCreatePlan}
                  startDate={startDate}
                  endDate={endDate}
                />
              )}
            </NineBoxGridWrapper>
          </div>
        </GridBordered>
        <NineBoxSidebar
          planHighlight={planHighlight}
          selectedDataPoint={selectedDataPoint}
          startDate={startDate}
          endDate={endDate}
          onCreatePlan={() => onCreatePlan(selectedDataPoint!.rep)}
          meetingTime={meetingTime}
          reviewTime={reviewTime}
          repPlans={repPlans}
          skillChanges={repSkillChanges(repPlans, velocityMap, timeSpentByPlan)}
          adminId={adminId}
          managerId={managerToDisplay?.id}
          repDataLoading={repDataLoading}
        />
      </Grid>
    </div>
  );
};

const useStyles = makeStyles(() => ({
  nineBoxWrap: {
    margin: '0 -4px -4px -2px',
  },
  root: {
    flexGrow: 1,
    marginBottom: 8,
  },
}));
